import React, { useState, useEffect } from 'react';
import { showSuccess, showInfo, showError } from '../../utils/notifications';
import { getContextualError, logError } from '../../utils/errorHandling';
import { returnPass } from '../../api/studentPassesApi';
import styles from './ActivePassTimer.module.css';

// The pass fields ActivePassTimer actually reads. A full PassResponse (as passed
// by StudentDashboard) structurally satisfies this.
interface ActivePassTimerPass {
  id: number;
  destinationName: string;
  approverName: string | null;
  activatedAt: string | null;
}

interface ActivePassTimerProps {
  pass: ActivePassTimerPass | null;
  onReturned?: () => void | Promise<void>;
}

const ActivePassTimer = ({
  pass,
  onReturned,
}: ActivePassTimerProps): React.ReactElement | null => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isReturning, setIsReturning] = useState(false);

  // Calculate elapsed time since pass was activated
  useEffect(() => {
    if (!pass || !pass.activatedAt) return;

    const startTime = new Date(pass.activatedAt).getTime();

    const updateTimer = () => {
      const now = new Date().getTime();
      // Clamp to >= 0: if activatedAt is slightly ahead of this client's clock
      // (minor device/server skew), (now - startTime) is negative and would
      // otherwise render as "-1:-30"-style garbage until the clock catches up.
      const elapsed = Math.max(0, Math.floor((now - startTime) / 1000)); // seconds elapsed
      setElapsedTime(elapsed);
    };

    // Update immediately
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [pass]);

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleReturnFromPass = async () => {
    if (!pass || isReturning) return;

    setIsReturning(true);
    try {
      await returnPass(pass.id);
      showSuccess('You have successfully returned from your pass!');
      if (onReturned) {
        await onReturned();
      }
    } catch (error) {
      if ((error as { status?: number }).status === 409) {
        // Pass was already returned/expired server-side, possibly racing a
        // pass:expired/pass:returned socket event that arrived just before
        // the click - nothing to alarm the student about. Still resync so
        // the UI doesn't stay stuck on a live timer for a closed pass.
        showInfo('Pass already closed');
        if (onReturned) {
          await onReturned();
        }
      } else {
        logError(error, 'returnFromPass');
        showError(getContextualError(error, 'pass-return'));
      }
    } finally {
      setIsReturning(false);
    }
  };

  if (!pass) return null;

  return (
    <div className={styles['active-pass-timer']}>
      <div className={styles['timer-header']}>
        <h2>You are currently out</h2>
        <p>
          Destination: <strong>{pass.destinationName}</strong>
        </p>
        <p>
          Approved by: <strong>{pass.approverName}</strong>
        </p>
      </div>

      <div className={styles['timer-display']}>
        <div className={styles['timer-label']}>Time Out</div>
        <div className={styles['timer-time']}>{formatTime(elapsedTime)}</div>
      </div>

      <div className={styles['timer-footer']}>
        <button
          className={styles['return-button']}
          onClick={handleReturnFromPass}
          disabled={isReturning}
        >
          {isReturning ? 'Returning...' : 'Mark Returned'}
        </button>
      </div>
    </div>
  );
};

export default ActivePassTimer;
